import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Loader2, BookOpen, Target, Layers, ArrowRight, Sparkles, ClipboardPaste, Library } from 'lucide-react';
import { analyzeText } from '../services/api';
import { AnalysisData, UnitContext } from '../types';
import { useSoundHandlers } from '../hooks/useSoundEffects';
import { useTheme } from '../contexts/ThemeContext';
import CourseSelector, { SelectedSentenceInfo } from '../components/CourseSelector';

type InputMode = 'paste' | 'course';

interface HomePageProps {
  onAnalysisComplete: (data: AnalysisData, unitContext: UnitContext | null) => void;
}

const sampleSentences = [
  "Behaviorists suggest that the child who is raised in an environment where there are many stimuli which develop his or her capacity for appropriate responses will experience greatly intellectual development.",
  "The scientist who discovered the vaccine that saved millions of lives was awarded the Nobel Prize, which she had dreamed of receiving since childhood.",
  "Although the company faced significant challenges during the pandemic, the innovative strategies that were implemented by the new management team eventually led to a remarkable recovery.",
];

export default function HomePage({ onAnalysisComplete }: HomePageProps) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputMode, setInputMode] = useState<InputMode>('paste');
  const [selectedSentenceInfo, setSelectedSentenceInfo] = useState<SelectedSentenceInfo | null>(null);
  const navigate = useNavigate();
  const { playHover, playClick } = useSoundHandlers();
  const { theme } = useTheme();
  const isQinghua = theme === 'qinghua';

  // 从课程选择器接收句子和相关信息
  const handleSentenceSelect = (sentence: string, info?: SelectedSentenceInfo) => {
    setText(sentence);
    setSelectedSentenceInfo(info || null);
  };

  // 切换输入模式
  const handleModeChange = (mode: InputMode) => {
    playClick();
    setInputMode(mode);
    setError('');
    if (mode === 'paste') {
      setSelectedSentenceInfo(null);
    }
  };

  const handleAnalyze = async () => {
    if (!text.trim()) {
      setError('请输入要分析的句子');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await analyzeText({ text: text.trim() });
      
      // 构建单元上下文（仅在课程模式下）
      let unitContext: UnitContext | null = null;
      if (inputMode === 'course' && selectedSentenceInfo) {
        unitContext = {
          unitId: selectedSentenceInfo.unitId,
          unitName: selectedSentenceInfo.unitName,
          courseId: selectedSentenceInfo.courseId,
          courseName: selectedSentenceInfo.courseName,
          currentSentenceId: selectedSentenceInfo.sentenceId,
          currentSentenceOrder: selectedSentenceInfo.orderNum,
        };
      }
      
      onAnalysisComplete(data, unitContext);
      navigate('/practice');
    } catch (err) {
      setError('分析失败，请确保后端服务已启动');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSampleClick = (sentence: string) => {
    setText(sentence);
  };

  const steps = [
    { step: 1, title: '过程识别', icon: Target, desc: '找出句中动词' },
    { step: 2, title: '对象识别', icon: BookOpen, desc: '识别名词词组' },
    { step: 3, title: '建立连接', icon: ArrowRight, desc: '动宾主谓关系' },
    { step: 4, title: '句子拆解', icon: Layers, desc: '分层小句结构' },
    { step: 5, title: '逐层翻译', icon: Sparkles, desc: '由内向外翻译' },
  ];

  // 青花瓷主题的弧形步骤位置
  const arcPositions = [
    { top: '0%', left: '50%', transform: 'translateX(-50%)' },
    { top: '15%', right: '8%', transform: 'none' },
    { bottom: '5%', right: '18%', transform: 'none' },
    { bottom: '5%', left: '18%', transform: 'none' },
    { top: '15%', left: '8%', transform: 'none' },
  ];

  // 青花瓷主题布局
  if (isQinghua) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* 青花瓷 Hero Section - 书法标题 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* 装饰性标签 */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8"
            style={{
              background: '#FFFDF8',
              border: '2px solid rgba(46, 90, 168, 0.3)',
              boxShadow: '0 2px 10px rgba(46, 90, 168, 0.1)'
            }}
          >
            <span style={{ color: '#C9A961', fontSize: '1rem' }}>❋</span>
            <span style={{ color: '#2E5AA8', fontFamily: "'Noto Serif SC', serif", fontSize: '0.9rem' }}>
              智能句法分析
            </span>
            <span style={{ color: '#C9A961', fontSize: '1rem' }}>❋</span>
          </motion.div>
          
          {/* 书法标题 */}
          <h1 
            className="mb-6"
            style={{ 
              fontFamily: "'Ma Shan Zheng', serif",
              fontSize: '3.5rem',
              letterSpacing: '0.1em'
            }}
          >
            <span style={{ color: '#2C3E50' }}>长难句</span>
            <span style={{ color: '#2E5AA8', marginLeft: '0.5rem' }}>五步分析法</span>
          </h1>
          
          <p 
            className="max-w-2xl mx-auto leading-relaxed"
            style={{ 
              color: '#4A5568',
              fontFamily: "'Noto Serif SC', serif",
              fontSize: '1.1rem'
            }}
          >
            通过系统化的五个步骤，逐层剖析英语长难句的结构，
            <br />掌握句子的核心框架与逻辑关系
          </p>
        </motion.div>

        {/* 青花瓷 五步导览 - 半圆弧形布局 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="relative mb-16"
          style={{ height: '240px' }}
        >
          {/* 中心装饰圆 */}
          <div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full"
            style={{
              background: 'linear-gradient(135deg, #F8F4E8 0%, #FFFDF8 100%)',
              border: '2px dashed rgba(46, 90, 168, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span style={{ color: '#2E5AA8', fontFamily: "'Ma Shan Zheng', serif", fontSize: '1.2rem' }}>五步</span>
          </div>

          {/* 连接弧线 */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none" 
            style={{ opacity: 0.3 }}
          >
            <path
              d="M 120 180 Q 250 20 500 20 Q 750 20 880 180"
              fill="none"
              stroke="#2E5AA8"
              strokeWidth="2"
              strokeDasharray="8 4"
            />
          </svg>

          {steps.map((item, index) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
              whileHover={{ scale: 1.1, zIndex: 10 }}
              onMouseEnter={playHover}
              className="absolute cursor-pointer"
              style={{
                ...arcPositions[index],
                width: '80px',
                height: '80px',
              }}
            >
              <div 
                className="w-full h-full rounded-full flex flex-col items-center justify-center transition-all duration-300"
                style={{
                  background: '#FFFDF8',
                  border: '2px solid rgba(46, 90, 168, 0.4)',
                  boxShadow: '0 4px 15px rgba(46, 90, 168, 0.1)'
                }}
              >
                <span 
                  style={{ 
                    color: '#2E5AA8', 
                    fontFamily: "'Ma Shan Zheng', serif",
                    fontSize: '1.5rem',
                    lineHeight: 1
                  }}
                >
                  {item.step}
                </span>
                <span 
                  style={{ 
                    color: '#4A5568',
                    fontFamily: "'Noto Serif SC', serif",
                    fontSize: '0.65rem',
                    marginTop: '2px'
                  }}
                >
                  {item.title}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* 青花瓷 输入区域 - 古籍书页样式 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="qinghua-page p-8 mb-8"
          style={{ paddingLeft: '2rem' }}
        >
          {/* Tab 切换 */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <button
              onClick={() => handleModeChange('paste')}
              onMouseEnter={playHover}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300"
              style={{
                background: inputMode === 'paste' ? 'rgba(46, 90, 168, 0.1)' : 'transparent',
                border: `2px solid ${inputMode === 'paste' ? '#2E5AA8' : 'rgba(46, 90, 168, 0.2)'}`,
                color: inputMode === 'paste' ? '#2E5AA8' : '#718096',
                fontFamily: "'Noto Serif SC', serif"
              }}
            >
              <ClipboardPaste className="w-4 h-4" />
              <span>粘贴句子</span>
            </button>
            <button
              onClick={() => handleModeChange('course')}
              onMouseEnter={playHover}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300"
              style={{
                background: inputMode === 'course' ? 'rgba(46, 90, 168, 0.1)' : 'transparent',
                border: `2px solid ${inputMode === 'course' ? '#2E5AA8' : 'rgba(46, 90, 168, 0.2)'}`,
                color: inputMode === 'course' ? '#2E5AA8' : '#718096',
                fontFamily: "'Noto Serif SC', serif"
              }}
            >
              <Library className="w-4 h-4" />
              <span>从课程选择</span>
            </button>
          </div>

          {/* 粘贴模式 */}
          {inputMode === 'paste' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="在此输入要分析的英语长难句..."
                className="cyber-input min-h-[150px] resize-none mb-4 text-lg"
                style={{
                  background: 'rgba(248, 244, 232, 0.5)',
                  fontFamily: "'Noto Serif SC', serif"
                }}
                disabled={loading}
              />
            </motion.div>
          )}

          {/* 课程选择模式 */}
          {inputMode === 'course' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="mb-4"
            >
              <CourseSelector 
                onSentenceSelect={handleSentenceSelect}
                disabled={loading}
              />
              {text && (
                <div 
                  className="mt-4 p-4 rounded-lg"
                  style={{
                    background: 'rgba(46, 90, 168, 0.05)',
                    border: '1px solid rgba(46, 90, 168, 0.2)'
                  }}
                >
                  <p 
                    className="text-sm mb-2"
                    style={{ color: '#718096', fontFamily: "'Noto Serif SC', serif" }}
                  >
                    已选句子：
                  </p>
                  <p 
                    style={{ 
                      color: '#2C3E50',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '0.9rem',
                      lineHeight: 1.6
                    }}
                  >
                    {text}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ color: '#c24b40', fontSize: '0.875rem', marginBottom: '1rem' }}
            >
              {error}
            </motion.p>
          )}

          <div className="flex items-center justify-between">
            <div style={{ color: '#718096', fontFamily: "'Noto Serif SC', serif", fontSize: '0.875rem' }}>
              {text.length > 0 && (
                <span>{text.split(/\s+/).filter(Boolean).length} 词</span>
              )}
            </div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => { playClick(); handleAnalyze(); }}
              onMouseEnter={playHover}
              disabled={loading || !text.trim()}
              className="cyber-button-primary qinghua-stamp flex items-center gap-2 disabled:opacity-50"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>分析中...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>开始分析</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* 青花瓷 示例句子 - 书签样式 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <p 
            className="mb-4 text-center"
            style={{ 
              color: '#718096',
              fontFamily: "'Noto Serif SC', serif",
              fontSize: '0.9rem'
            }}
          >
            示例句子
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sampleSentences.map((sentence, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 + index * 0.1 }}
                whileHover={{ y: -8 }}
                onClick={() => { playClick(); handleSampleClick(sentence); }}
                onMouseEnter={playHover}
                className="qinghua-bookmark p-5 text-left"
              >
                <div 
                  className="mb-2"
                  style={{ 
                    color: '#2E5AA8',
                    fontFamily: "'Ma Shan Zheng', serif",
                    fontSize: '1rem'
                  }}
                >
                  例句 {index + 1}
                </div>
                <p 
                  style={{
                    color: '#4A5568',
                    fontFamily: "'Noto Serif SC', serif",
                    fontSize: '0.8rem',
                    lineHeight: 1.6
                  }}
                >
                  {sentence.length > 80 ? sentence.substring(0, 80) + '...' : sentence}
                </p>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  // 默认主题布局
  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
          style={{
            background: 'var(--theme-glow-color)',
            border: '1px solid var(--theme-border)'
          }}
        >
          <Sparkles className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
          <span className="text-sm font-body" style={{ color: 'var(--theme-accent)' }}>AI 驱动的句法分析</span>
        </motion.div>
        
        <h1 className="font-display text-5xl md:text-6xl font-bold mb-6">
          <span style={{ color: 'var(--theme-text-primary)' }}>长难句</span>
          <span className="ml-3" style={{ color: 'var(--theme-accent)' }}>五步分析法</span>
        </h1>
        
        <p className="text-xl font-body max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--theme-text-secondary)' }}>
          通过系统化的五个步骤，逐层剖析英语长难句的结构，
          掌握句子的核心框架与逻辑关系
        </p>
      </motion.div>

      {/* 步骤预览 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="grid grid-cols-5 gap-4 mb-16"
      >
        {steps.map((item, index) => (
          <motion.div
            key={item.step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
            onMouseEnter={playHover}
            className="cyber-card p-4 text-center transition-all duration-300 group"
            style={{ ['--hover-border' as string]: 'var(--theme-border-hover)' }}
          >
            <div className="step-indicator mx-auto mb-3 group-hover:active">
              {item.step}
            </div>
            <h3 className="font-display text-sm mb-1" style={{ color: 'var(--theme-accent)' }}>{item.title}</h3>
            <p className="text-xs font-body" style={{ color: 'var(--theme-text-muted)' }}>{item.desc}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* 输入区域 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="cyber-card p-8 scanline"
      >
        {/* Tab 切换 */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => handleModeChange('paste')}
            onMouseEnter={playHover}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300"
            style={{
              background: inputMode === 'paste' ? 'var(--theme-glow-strong)' : 'transparent',
              border: `1px solid ${inputMode === 'paste' ? 'var(--theme-accent)' : 'var(--theme-border)'}`,
              color: inputMode === 'paste' ? 'var(--theme-accent)' : 'var(--theme-text-muted)'
            }}
          >
            <ClipboardPaste className="w-4 h-4" />
            <span className="font-display">粘贴句子</span>
          </button>
          <button
            onClick={() => handleModeChange('course')}
            onMouseEnter={playHover}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300"
            style={{
              background: inputMode === 'course' ? 'var(--theme-glow-strong)' : 'transparent',
              border: `1px solid ${inputMode === 'course' ? 'var(--theme-accent)' : 'var(--theme-border)'}`,
              color: inputMode === 'course' ? 'var(--theme-accent)' : 'var(--theme-text-muted)'
            }}
          >
            <Library className="w-4 h-4" />
            <span className="font-display">从课程选择</span>
          </button>
        </div>

        {/* 粘贴模式 */}
        {inputMode === 'paste' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="在此输入要分析的英语长难句..."
              className="cyber-input min-h-[150px] resize-none mb-4 text-lg"
              disabled={loading}
            />
          </motion.div>
        )}

        {/* 课程选择模式 */}
        {inputMode === 'course' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="mb-4"
          >
            <CourseSelector 
              onSentenceSelect={handleSentenceSelect}
              disabled={loading}
            />
            {text && (
              <div 
                className="mt-4 p-4 rounded-lg"
                style={{
                  background: 'var(--theme-glow-color)',
                  border: '1px solid var(--theme-border)'
                }}
              >
                <p className="text-sm mb-2" style={{ color: 'var(--theme-text-muted)' }}>
                  已选句子：
                </p>
                <p 
                  className="font-mono text-sm leading-relaxed"
                  style={{ color: 'var(--theme-text-primary)' }}
                >
                  {text}
                </p>
              </div>
            )}
          </motion.div>
        )}

        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-400 text-sm mb-4"
          >
            {error}
          </motion.p>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
            {text.length > 0 && (
              <span>{text.split(/\s+/).filter(Boolean).length} 词</span>
            )}
          </div>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => { playClick(); handleAnalyze(); }}
            onMouseEnter={playHover}
            disabled={loading || !text.trim()}
            className="cyber-button-primary flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-display">分析中...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span className="font-display">开始分析</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* 示例句子 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="mt-8"
      >
        <p className="text-sm mb-4 font-body" style={{ color: 'var(--theme-text-muted)' }}>示例句子：</p>
        <div className="space-y-3">
          {sampleSentences.map((sentence, index) => (
            <motion.button
              key={index}
              whileHover={{ x: 5 }}
              onClick={() => { playClick(); handleSampleClick(sentence); }}
              onMouseEnter={playHover}
              className="block w-full text-left p-4 rounded-lg transition-all duration-300 font-mono text-sm"
              style={{
                background: 'var(--theme-glow-color)',
                border: '1px solid var(--theme-border)',
                color: 'var(--theme-text-secondary)'
              }}
            >
              <span style={{ color: 'var(--theme-accent)' }} className="mr-2">→</span>
              {sentence.length > 100 ? sentence.substring(0, 100) + '...' : sentence}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

